/**
 * ModernizeIT API Port Interface
 * Defines how the core interacts with the ModernizeIT API layer
 *
 * API Base: http://localhost:8000
 *
 * Implementations:
 * - ModernizeAPIAdapter (REST client)
 * - MockModernizeAPIAdapter (Development/Testing)
 */

export const IModernizeAPIPort = {
  // ===== Health =====

  /**
   * Check API health
   * @returns {Promise<{status: string}>}
   */
  healthCheck: async () => {
    throw new Error('IModernizeAPIPort.healthCheck not implemented')
  },

  // ===== Ingest =====

  /**
   * Upload source files to start ingest
   * POST /ingest/upload
   * @param {Object} params
   * @param {string} params.scoutAccountId - e.g., "EVH"
   * @param {string} params.applicationName - e.g., "TestApp01"
   * @param {string} params.sourcePath - S3 or local path to source files
   * @param {string} [params.sourceLanguage='cobol']
   * @param {string} [params.targetLanguage='java']
   * @param {boolean} [params.generateTypeMappings=true]
   * @returns {Promise<{jobId: string}>}
   */
  startIngest: async (params) => {
    throw new Error('IModernizeAPIPort.startIngest not implemented')
  },

  /**
   * Get ingest job status
   * GET /ingest/jobs/{jobId}/status
   * @param {string} jobId
   * @returns {Promise<{status: string, progress: number, message: string}>}
   */
  getIngestStatus: async (jobId) => {
    throw new Error('IModernizeAPIPort.getIngestStatus not implemented')
  },

  /**
   * Get ingest results
   * GET /ingest/results/{jobId}
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getIngestResults: async (jobId) => {
    throw new Error('IModernizeAPIPort.getIngestResults not implemented')
  },

  // ===== Code Analysis =====

  /**
   * Run code analysis
   * POST /codeanalysis
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @param {string} [params.sourceHash]
   * @param {string} [params.mainProgram]
   * @param {boolean} [params.generateJava=true]
   * @param {boolean} [params.generateGraphs=true]
   * @returns {Promise<{jobId: string}>}
   */
  startCodeAnalysis: async (params) => {
    throw new Error('IModernizeAPIPort.startCodeAnalysis not implemented')
  },

  /**
   * Get code analysis status
   * GET /codeanalysis/{jobId}/status
   * @param {string} jobId
   * @returns {Promise<{status: string, progress: number, message: string}>}
   */
  getCodeAnalysisStatus: async (jobId) => {
    throw new Error('IModernizeAPIPort.getCodeAnalysisStatus not implemented')
  },

  /**
   * Get code analysis results
   * GET /codeanalysis/{jobId}/results
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getCodeAnalysisResults: async (jobId) => {
    throw new Error('IModernizeAPIPort.getCodeAnalysisResults not implemented')
  },

  /**
   * Get code analysis JSON artifact
   * GET /codeanalysis/{jobId}/results/json/{filename}
   * @param {string} jobId
   * @param {string} filename
   * @returns {Promise<Object>}
   */
  getCodeAnalysisArtifact: async (jobId, filename) => {
    throw new Error('IModernizeAPIPort.getCodeAnalysisArtifact not implemented')
  },

  /**
   * Get code analysis graph image
   * GET /codeanalysis/{jobId}/results/graphs/{filename}
   * @param {string} jobId
   * @param {string} filename
   * @returns {Promise<Blob>}
   */
  getCodeAnalysisGraph: async (jobId, filename) => {
    throw new Error('IModernizeAPIPort.getCodeAnalysisGraph not implemented')
  },

  // ===== Code Refactor =====

  /**
   * Run refactor analysis
   * POST /coderefactor
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @param {string} [params.className]
   * @param {string} [params.mode='analyze'] - 'analyze' or 'transform'
   * @param {boolean} [params.useAI=true]
   * @param {string} [params.recipesToApply]
   * @returns {Promise<{jobId: string}>}
   */
  startCodeRefactor: async (params) => {
    throw new Error('IModernizeAPIPort.startCodeRefactor not implemented')
  },

  /**
   * Transform code with recipes
   * POST /coderefactor/transform
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @param {string} [params.recipesToApply='all']
   * @returns {Promise<{jobId: string}>}
   */
  transformCode: async (params) => {
    throw new Error('IModernizeAPIPort.transformCode not implemented')
  },

  /**
   * Get code refactor status
   * GET /coderefactor/{jobId}/status
   * @param {string} jobId
   * @returns {Promise<{status: string, progress: number, message: string}>}
   */
  getCodeRefactorStatus: async (jobId) => {
    throw new Error('IModernizeAPIPort.getCodeRefactorStatus not implemented')
  },

  /**
   * Get code refactor results
   * GET /coderefactor/{jobId}/results
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getCodeRefactorResults: async (jobId) => {
    throw new Error('IModernizeAPIPort.getCodeRefactorResults not implemented')
  },

  /**
   * Get refactor recipes
   * GET /coderefactor/{jobId}/results/recipes
   * @param {string} jobId
   * @returns {Promise<Array>}
   */
  getCodeRefactorRecipes: async (jobId) => {
    throw new Error('IModernizeAPIPort.getCodeRefactorRecipes not implemented')
  },

  // ===== Dependency Mapper =====

  /**
   * Run dependency mapping
   * POST /dependencymapper
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @param {string} params.sourceType - 'cobol' or 'java'
   * @returns {Promise<{jobId: string}>}
   */
  startDependencyMapper: async (params) => {
    throw new Error('IModernizeAPIPort.startDependencyMapper not implemented')
  },

  /**
   * Compare COBOL vs Java dependencies
   * POST /dependencymapper/compare
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @returns {Promise<{jobId: string}>}
   */
  compareDependencies: async (params) => {
    throw new Error('IModernizeAPIPort.compareDependencies not implemented')
  },

  /**
   * Get dependency mapper status
   * @param {string} jobId
   * @returns {Promise<{status: string, progress: number, message: string}>}
   */
  getDependencyMapperStatus: async (jobId) => {
    throw new Error('IModernizeAPIPort.getDependencyMapperStatus not implemented')
  },

  /**
   * Get dependency mapper results
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getDependencyMapperResults: async (jobId) => {
    throw new Error('IModernizeAPIPort.getDependencyMapperResults not implemented')
  },

  // ===== Monolith Identifier =====

  /**
   * Run monolith identification
   * POST /monolithidentifier
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @param {string} params.sourceType - 'cobol' or 'java'
   * @returns {Promise<{jobId: string}>}
   */
  startMonolithIdentifier: async (params) => {
    throw new Error('IModernizeAPIPort.startMonolithIdentifier not implemented')
  },

  /**
   * Compare COBOL vs Java monolith patterns
   * POST /monolithidentifier/compare
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @returns {Promise<{jobId: string}>}
   */
  compareMonolithPatterns: async (params) => {
    throw new Error('IModernizeAPIPort.compareMonolithPatterns not implemented')
  },

  /**
   * Get monolith identifier status
   * @param {string} jobId
   * @returns {Promise<{status: string, progress: number, message: string}>}
   */
  getMonolithIdentifierStatus: async (jobId) => {
    throw new Error('IModernizeAPIPort.getMonolithIdentifierStatus not implemented')
  },

  /**
   * Get monolith identifier results
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getMonolithIdentifierResults: async (jobId) => {
    throw new Error('IModernizeAPIPort.getMonolithIdentifierResults not implemented')
  },

  // ===== Data Analysis =====

  /**
   * Run data analysis
   * POST /dataanalysis
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @returns {Promise<{jobId: string}>}
   */
  startDataAnalysis: async (params) => {
    throw new Error('IModernizeAPIPort.startDataAnalysis not implemented')
  },

  /**
   * Get data analysis status
   * @param {string} jobId
   * @returns {Promise<{status: string, progress: number, message: string}>}
   */
  getDataAnalysisStatus: async (jobId) => {
    throw new Error('IModernizeAPIPort.getDataAnalysisStatus not implemented')
  },

  /**
   * Get data analysis results
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getDataAnalysisResults: async (jobId) => {
    throw new Error('IModernizeAPIPort.getDataAnalysisResults not implemented')
  },

  /**
   * Get ERD diagram
   * GET /dataanalysis/{jobId}/results/erd
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getERD: async (jobId) => {
    throw new Error('IModernizeAPIPort.getERD not implemented')
  },

  /**
   * Get data lineage
   * GET /dataanalysis/{jobId}/results/lineage
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getDataLineage: async (jobId) => {
    throw new Error('IModernizeAPIPort.getDataLineage not implemented')
  },

  // ===== Discovery =====

  /**
   * Run discovery analysis
   * POST /discovery/analyze
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @param {boolean} [params.enableAI=true]
   * @param {number} [params.currentMips]
   * @param {number} [params.annualMainframeCost]
   * @param {number} [params.cobolDeveloperCount]
   * @param {number} [params.cobolDeveloperSalary]
   * @returns {Promise<{jobId: string}>}
   */
  startDiscovery: async (params) => {
    throw new Error('IModernizeAPIPort.startDiscovery not implemented')
  },

  /**
   * Calculate ROI
   * POST /discovery/roi/calculate
   * @param {Object} params
   * @returns {Promise<Object>}
   */
  calculateROI: async (params) => {
    throw new Error('IModernizeAPIPort.calculateROI not implemented')
  },

  /**
   * Get discovery results
   * GET /discovery/results/{jobId}
   * @param {string} jobId
   * @param {string} [section] - 'roi_analysis', 'roadmap', 'business_processes', 'integration_points'
   * @returns {Promise<Object>}
   */
  getDiscoveryResults: async (jobId, section) => {
    throw new Error('IModernizeAPIPort.getDiscoveryResults not implemented')
  },

  /**
   * Get ROI assumptions
   * GET /discovery/assumptions
   * @returns {Promise<Object>}
   */
  getROIAssumptions: async () => {
    throw new Error('IModernizeAPIPort.getROIAssumptions not implemented')
  },

  // ===== Architecture =====

  /**
   * Run architecture analysis
   * POST /architecture/analyze
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @param {string} [params.discoveryJobId]
   * @param {string} [params.dataAnalysisJobId]
   * @param {string} [params.codeAnalysisJobId]
   * @param {string} [params.codeRefactorJobId]
   * @param {boolean} [params.generateIaC=true]
   * @returns {Promise<{jobId: string}>}
   */
  startArchitecture: async (params) => {
    throw new Error('IModernizeAPIPort.startArchitecture not implemented')
  },

  /**
   * Get architecture status
   * @param {string} jobId
   * @returns {Promise<{status: string, progress: number, message: string}>}
   */
  getArchitectureStatus: async (jobId) => {
    throw new Error('IModernizeAPIPort.getArchitectureStatus not implemented')
  },

  /**
   * Get architecture results
   * GET /architecture/{jobId}/results
   * @param {string} jobId
   * @param {string} [section] - 'summary', 'cost_estimate', 'validation_report', 'traceability'
   * @returns {Promise<Object>}
   */
  getArchitectureResults: async (jobId, section) => {
    throw new Error('IModernizeAPIPort.getArchitectureResults not implemented')
  },

  /**
   * Get IaC templates list
   * GET /architecture/{jobId}/results/iac
   * @param {string} jobId
   * @returns {Promise<Array<string>>}
   */
  getIaCTemplates: async (jobId) => {
    throw new Error('IModernizeAPIPort.getIaCTemplates not implemented')
  },

  /**
   * Get IaC template file
   * GET /architecture/{jobId}/results/iac/{filename}
   * @param {string} jobId
   * @param {string} filename
   * @returns {Promise<string>}
   */
  getIaCTemplateFile: async (jobId, filename) => {
    throw new Error('IModernizeAPIPort.getIaCTemplateFile not implemented')
  },

  // ===== Service Generation =====

  /**
   * Start service generation
   * POST /servicegeneration
   * Generates Spring Boot services from Code Analysis monolith
   * Uses AI recommendations and ERD for proper architecture
   * @param {Object} params
   * @param {string} params.scoutAccountId
   * @param {string} params.applicationName
   * @param {string} [params.programName] - COBOL program name (auto-detected if not provided)
   * @param {string} [params.basePackage] - Java package (defaults to com.modernized.{app})
   * @param {boolean} [params.includeAllRepositories=true] - Inject all repositories
   * @param {boolean} [params.includeAllFields=true] - Include all fields
   * @param {boolean} [params.strictValidation=true] - Fail on validation issues
   * @returns {Promise<{job_id: string, status: string, message: string}>}
   */
  startServiceGeneration: async (params) => {
    throw new Error('IModernizeAPIPort.startServiceGeneration not implemented')
  },

  /**
   * Get service generation status
   * GET /servicegeneration/{jobId}
   * @param {string} jobId
   * @returns {Promise<{job_id: string, status: string, progress: Object}>}
   */
  getServiceGenerationStatus: async (jobId) => {
    throw new Error('IModernizeAPIPort.getServiceGenerationStatus not implemented')
  },

  /**
   * Get service generation results
   * GET /servicegeneration/{jobId}/results
   * @param {string} jobId
   * @returns {Promise<{services: Array, method_count: number, validation_passed: boolean}>}
   */
  getServiceGenerationResults: async (jobId) => {
    throw new Error('IModernizeAPIPort.getServiceGenerationResults not implemented')
  },

  /**
   * Download generated services as ZIP
   * GET /servicegeneration/{jobId}/download
   * @param {string} jobId
   * @returns {Promise<Blob>}
   */
  downloadServiceGeneration: async (jobId) => {
    throw new Error('IModernizeAPIPort.downloadServiceGeneration not implemented')
  },

  // ===== Generic Job Status =====

  /**
   * Get job status for any job type
   * @param {string} jobType - 'ingest', 'codeanalysis', etc.
   * @param {string} jobId
   * @returns {Promise<{status: string, progress: number, message: string}>}
   */
  getJobStatus: async (jobType, jobId) => {
    throw new Error('IModernizeAPIPort.getJobStatus not implemented')
  },

  /**
   * Get job results for any job type
   * @param {string} jobType - 'ingest', 'codeanalysis', etc.
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getJobResults: async (jobType, jobId) => {
    throw new Error('IModernizeAPIPort.getJobResults not implemented')
  },

  /**
   * Get JSON artifact for any job type
   * @param {string} jobType
   * @param {string} jobId
   * @param {string} filename
   * @returns {Promise<Object>}
   */
  getJobArtifact: async (jobType, jobId, filename) => {
    throw new Error('IModernizeAPIPort.getJobArtifact not implemented')
  },
}

/**
 * Create a ModernizeIT API port implementation
 */
export function createModernizeAPIPort(implementation) {
  const port = { ...IModernizeAPIPort }

  for (const key of Object.keys(IModernizeAPIPort)) {
    if (typeof implementation[key] === 'function') {
      port[key] = implementation[key]
    }
  }

  return port
}
